/**
 * Match-data coverage window.
 * Our earliest match row is 2000-08-18, so tenures that started earlier
 * only have their in-coverage matches counted.
 */
export const COVERAGE_START_LABEL = "the 2000/01 season";

const COVERAGE_START_ISO = "2000-07-01";

/** True when a tenure started before our match-data coverage begins. */
export const isTenureTruncated = (startDate: string): boolean =>
  startDate < COVERAGE_START_ISO;

export const TRUNCATED_TOOLTIP =
  "This tenure started before our data coverage (2000/01 season) — matches before that are not counted.";

export const COVERAGE_FOOTNOTE =
  "* Match data covers domestic league games from the 2000/01 season onward. Tenures that began earlier show only matches within our coverage.";

/**
 * First season we hold match data for, per league.
 * Most leagues start in 2005; the Premier League goes back to 2000/01 and the
 * recently added competitions only have data from 2024/25 onward.
 */
export const LEAGUE_COVERAGE_START: Record<string, string> = {
  ENG_PL: "2000/01",
  AUT_BL: "2005/06",
  BEL_PL: "2005/06",
  CRO_1: "2005/06",
  DEN_SL: "2005/06",
  ENG_CH: "2005/06",
  ESP_L2: "2005/06",
  ESP_LL: "2005/06",
  FRA_L1: "2005/06",
  GER_BL: "2005/06",
  ITA_SA: "2005/06",
  ITA_SB: "2005/06",
  NED_ED: "2005/06",
  POL_EK: "2005/06",
  POR_LP: "2005/06",
  SCO_PL: "2005/06",
  SUI_SL: "2005/06",
  TUR_SL: "2005/06",
  NOR_EL: "2005",
  SWE_AL: "2005",
  FRA_L2: "2005/06",
  GER_B2: "2005/06",
  ENG_WSL: "2017/18",
  FRA_D1F: "2018/19",
  GER_FBL: "2019/20",
};

/**
 * First season we hold manager assignment data for, per league.
 * This is usually shallower than match coverage — several leagues have twenty
 * years of fixtures but only eight to ten years of tenures to attach to them.
 */
export const LEAGUE_MANAGER_COVERAGE_START: Record<string, string> = {
  ENG_PL: "1986/87",
  ENG_CH: "1999/00",
  GER_BL: "1998/99",
  ITA_SA: "2001/02",
  ITA_SB: "2004/05",
  ESP_LL: "2003/04",
  ESP_L2: "2005/06",
  FRA_L1: "2001/02",
  FRA_L2: "2012/13",
  GER_B2: "2012/13",
  SCO_PL: "2001/02",
  DEN_SL: "2003/04",
  CRO_1: "2012/13",
  BEL_PL: "2017/18",
  AUT_BL: "2020/21",
  NED_ED: "2015/16",
  POL_EK: "2015/16",
  POR_LP: "2011/12",
  SUI_SL: "2018/19",
  TUR_SL: "2015/16",
  NOR_EL: "2004",
  SWE_AL: "2009",
  ENG_WSL: "2017/18",
  FRA_D1F: "2018/19",
  GER_FBL: "2019/20",
};

/** Plain-language coverage note for a league, or null when unknown. */
export const leagueCoverageNote = (leagueCode?: string | null): string | null => {
  if (!leagueCode) return null;
  const matches = LEAGUE_COVERAGE_START[leagueCode];
  if (!matches) return null;
  const managers = LEAGUE_MANAGER_COVERAGE_START[leagueCode];
  if (managers && managers !== matches) {
    return `Coverage for this competition: results from ${matches}, manager records from ${managers}.`;
  }
  return `League match data for this competition starts with the ${matches} season.`;
};

/** Manager-record-only note, for manager and career views. */
export const managerCoverageNote = (leagueCode?: string | null): string | null => {
  if (!leagueCode) return null;
  const managers = LEAGUE_MANAGER_COVERAGE_START[leagueCode];
  if (!managers) return null;
  return `Manager records for this competition start with the ${managers} season.`;
};


